import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { authService } from '../services/authService';
import { Contract, Customer, Invoice } from '../types';
import Modal from '../components/Modal';
import ModernInvoicePrintButton from '../components/ModernInvoicePrintButton';
import BoletoButton from '../components/BoletoButton';
import InvoiceFormEnhanced from '../components/InvoiceFormEnhanced';
import SortableTable, { SortableColumn } from '../components/SortableTable';
import SearchInput from '../components/SearchInput';
import { clearInvoiceCache } from '../hooks/useInvoices';

// Extended Invoice type for display purposes
interface InvoiceDisplay extends Invoice {
  contract_name: string;
  customer_name: string;
}

type ConfirmActionType =
  | 'send-invoice'
  | 'send-late-payment'
  | 'bulk-send-updated-boletos'
  | 'bulk-send-emails'
  | 'bulk-send-to-accountant'
  | 'bulk-send-to-accountant-individual'
  | 'bulk-upload-to-skalar'
  | 'bulk-upload-to-skalar-individual'
  | 'bulk-post';

interface ConfirmActionState {
  type: ConfirmActionType;
  title: string;
  message: string;
  confirmLabel: string;
  invoiceId?: string;
  recipientEmail?: string;
  invoiceIds?: string[];
  accountantEmail?: string;
  accountantCurrencies?: string[];
}

interface MarkPaidState {
  invoiceId: string;
  invoiceLabel: string;
}

const AVAILABLE_CURRENCIES = ['EUR', 'USD', 'GBP', 'BRL', 'CAD', 'CHF'] as const;

const Invoices: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<InvoiceDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelModalAction, setCancelModalAction] = useState<'cancel' | 'void'>('cancel');
  const [cancelModalInvoiceIds, setCancelModalInvoiceIds] = useState<string[]>([]);
  const [cancelModalTargetLabel, setCancelModalTargetLabel] = useState('');
  const [cancelModalError, setCancelModalError] = useState<string | null>(null);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [actionFeedback, setActionFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmActionState, setConfirmActionState] = useState<ConfirmActionState | null>(null);
  const [confirmActionLoading, setConfirmActionLoading] = useState(false);
  const [markPaidState, setMarkPaidState] = useState<MarkPaidState | null>(null);
  const [markPaidMethod, setMarkPaidMethod] = useState('bank_transfer');
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [showDateRangeExportModal, setShowDateRangeExportModal] = useState(false);
  const [dateRangeExportFromDate, setDateRangeExportFromDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateRangeExportToDate, setDateRangeExportToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateRangeExportTaskId, setDateRangeExportTaskId] = useState<string | null>(null);
  const [dateRangeExportStatus, setDateRangeExportStatus] = useState<'idle' | 'starting' | 'running' | 'success' | 'failure'>('idle');
  const [dateRangeExportError, setDateRangeExportError] = useState<string | null>(null);
  const [dateRangeExportSummary, setDateRangeExportSummary] = useState<string | null>(null);
  const [dateRangeExportDownloading, setDateRangeExportDownloading] = useState(false);
  const [dateRangeExportCurrencies, setDateRangeExportCurrencies] = useState<string[]>([...AVAILABLE_CURRENCIES]);
  
  // Accountant email sending with individual emails (background task)
  const [accountantSendTaskId, setAccountantSendTaskId] = useState<string | null>(null);
  const [accountantSendStatus, setAccountantSendStatus] = useState<'idle' | 'starting' | 'running' | 'success' | 'failure'>('idle');
  const [accountantSendError, setAccountantSendError] = useState<string | null>(null);
  const [accountantSendSummary, setAccountantSendSummary] = useState<string | null>(null);
  const [accountantSendProgress, setAccountantSendProgress] = useState<{processed: number, total: number, current_item?: string, current_invoice_id?: string, status?: string} | null>(null);
  const [showAccountantSendModal, setShowAccountantSendModal] = useState(false);

  // Skalar API upload (background task)
  const [skalarUploadTaskId, setSkalarUploadTaskId] = useState<string | null>(null);
  const [skalarUploadStatus, setSkalarUploadStatus] = useState<'idle' | 'starting' | 'running' | 'success' | 'failure'>('idle');
  const [skalarUploadError, setSkalarUploadError] = useState<string | null>(null);
  const [skalarUploadSummary, setSkalarUploadSummary] = useState<string | null>(null);
  const [skalarUploadProgress, setSkalarUploadProgress] = useState<{processed: number, total: number, current_item?: string, current_invoice_id?: string, status?: string} | null>(null);
  const [showSkalarUploadModal, setShowSkalarUploadModal] = useState(false);

  // Invoice filters (only applied when true)
  const [filters, setFilters] = useState({
    show_draft: false,
    show_finalized: false,
    show_cancelled: false,
    show_voided: false,
  });
  // Single payment filter to avoid contradictory combinations
  const [paymentFilter, setPaymentFilter] = useState<'any' | 'paid' | 'unpaid' | 'overdue'>('any');

  const applyFilters = async () => {
    await fetchInvoices();
  };

  const clearFiltersUI = async () => {
    setFilters({
      show_draft: false,
      show_finalized: false,
      show_cancelled: false,
      show_voided: false,
    });
    setPaymentFilter('any');
    setSelectedCurrencies([]);
    await fetchInvoices();
  };

  // Load all data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchInvoices();

        // Handle navigation from contract page
        if (location.state?.contractId) {
          fetchContractForInvoice(location.state.contractId);
        }
      } catch (error) {
        console.error('Error loading invoice data:', error);
      }
    };
    
    loadData();
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const state = location.state as { flashFeedback?: { type: 'success' | 'error'; text: string } } | null;
    if (!state?.flashFeedback) {
      return;
    }

    setActionFeedback(state.flashFeedback);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  // Poll task status for accountant email sending
  useEffect(() => {
    if (!showAccountantSendModal || !accountantSendTaskId || (accountantSendStatus !== 'starting' && accountantSendStatus !== 'running')) {
      return;
    }

    // Set up polling
    const pollInterval = setInterval(() => {
      checkAccountantSendTaskStatus();
    }, 3000); // Poll every 3 seconds

    // Initial check
    checkAccountantSendTaskStatus();

    return () => {
      clearInterval(pollInterval);
    };
  }, [showAccountantSendModal, accountantSendTaskId, accountantSendStatus]);

  // Filter and search invoices
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => 
        invoice.invoice_number?.toString().toLowerCase().includes(searchLower) ||
        invoice.invoice_name?.toLowerCase().includes(searchLower) ||
        invoice.customer_name?.toLowerCase().includes(searchLower) ||
        invoice.invoice_status?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [invoices, searchTerm]);

  const selectedInvoiceCurrencyOptions = useMemo(() => {
    const selectedCurrencySet = new Set(
      invoices
        .filter((invoice) => selectedInvoices.has(invoice.invoice_id))
        .map((invoice) => String(invoice.currency || '').toUpperCase())
        .filter((currency) => currency.length > 0)
    );

    return AVAILABLE_CURRENCIES.filter((currency) => selectedCurrencySet.has(currency));
  }, [invoices, selectedInvoices]);

  const accountantCurrencyOptions = selectedInvoiceCurrencyOptions.length > 0
    ? [...selectedInvoiceCurrencyOptions]
    : [...AVAILABLE_CURRENCIES];

  // Get row style based on payment status
  const getRowStyle = (invoice: InvoiceDisplay) => {
    const today = new Date();
    const dueDate = invoice.due_date ? new Date(invoice.due_date) : null;
    const paymentStatus = invoice.payment_status as string;
    
    // Paid invoices - light green
    if (paymentStatus === 'paid') {
      return { backgroundColor: '#d4edda' };
    }

    if (paymentStatus === 'failed') {
      return { backgroundColor: '#f8d7da' };
    }
    
    // Overdue invoices - light red
    if (
      dueDate &&
      dueDate < today &&
      paymentStatus !== 'paid' &&
      paymentStatus !== 'processing' &&
      paymentStatus !== 'failed' &&
      paymentStatus !== 'cancelled' &&
      paymentStatus !== 'refunded'
    ) {
      return { backgroundColor: '#f8d7da' };
    }
    
    // Pending payment - light yellow
    if (paymentStatus === 'pending') {
      return { backgroundColor: '#fff3cd' };
    }
    
    return {};
  };

  // Define table columns
  const tableColumns: SortableColumn[] = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={selectedInvoices.size === filteredInvoices.length && filteredInvoices.length > 0}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedInvoices(new Set(filteredInvoices.map(inv => inv.invoice_id)));
            } else {
              setSelectedInvoices(new Set());
            }
          }}
        />
      ),
      sortable: false,
      render: (_, invoice) => (
        <input
          type="checkbox"
          checked={selectedInvoices.has(invoice.invoice_id)}
          onChange={(e) => {
            const newSelected = new Set(selectedInvoices);
            if (e.target.checked) {
              newSelected.add(invoice.invoice_id);
            } else {
              newSelected.delete(invoice.invoice_id);
            }
            setSelectedInvoices(newSelected);
          }}
        />
      )
    },
    {
      key: 'invoice_name',
      label: 'Invoice Number',
      render: (value, invoice) => {
        const paymentStatus = invoice.payment_status as string;
        return (
          <span style={{ fontWeight: '500' }}>
            {value || invoice.invoice_number || 'N/A'}
            {paymentStatus === 'paid' && ' ✓'}
            {isOverdue(invoice) && ' ⚠'}
          </span>
        );
      }
    },
        {
      key: 'customer_name',
      label: 'Customer',
      render: (value) => value || 'N/A'
    },
    {
      key: 'invoice_date',
      label: 'Invoice Date',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (value, invoice) => {
        const date = value ? new Date(value).toLocaleDateString() : 'N/A';
        const isOverdueInvoice = isOverdue(invoice);
        return (
          <span style={{ color: isOverdueInvoice ? '#dc3545' : 'inherit' }}>
            {date}
            {isOverdueInvoice && ' (Overdue)'}
          </span>
        );
      }
    },
    {
      key: 'latest_boleto_due_date',
      label: 'Boleto Due',
      render: (_value, invoice) => {
        const isBRL = invoice.currency === 'BRL';
        const raw: any = (invoice as any).latest_boleto_due_date;
        if (!isBRL) return '—';
        if (!raw) return 'N/A';
        const d = new Date(raw);
        const today = new Date();
        const overdue = d < today && (invoice.payment_status as string) !== 'paid';
        return (
          <span style={{ color: overdue ? '#dc3545' : 'inherit' }}>
            {d.toLocaleDateString()}
            {overdue && ' (Overdue)'}
          </span>
        );
      }
    },
    {
      key: 'total_amount',
      label: 'Amount',
      render: (value, invoice) => formatCurrency(value, invoice.currency)
    },
    {
      key: 'invoice_status',
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusBadgeClass(value)}`}>
          {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unknown'}
        </span>
      )
    },
    {
      key: 'payment_status',
      label: 'Payment',
      render: (value, invoice) => {
        const overdue = isOverdue(invoice);
        const statusClass = overdue ? 'badge-danger' : getPaymentStatusBadgeClass(value);
        const displayValue = overdue && value !== 'paid' ? 'Overdue' : value;
        return (
          <span className={`badge ${statusClass}`}>
            {displayValue ? displayValue.charAt(0).toUpperCase() + displayValue.slice(1) : 'Not Due'}
          </span>
        );
      }
    },
    {
      key: 'sending_status',
      label: 'Sent',
      render: (_value, invoice) => {
        const rawStatus = (invoice.sending_status as string) || 'not_sent';
        const status = rawStatus.toLowerCase();
        const sentDate = invoice.sent_date ? new Date(invoice.sent_date) : null;
        const deliveredDate = (invoice as any).delivery_confirmed_date ? new Date((invoice as any).delivery_confirmed_date) : null;
        const dueDate = invoice.due_date ? new Date(invoice.due_date) : null;

        let label = 'no';
        let badgeClass = 'badge-secondary';

        switch (status) {
          case 'pending_send':
            label = 'queued';
            badgeClass = 'badge-info';
            break;
          case 'sent':
          case 'resent':
            if ((invoice.invoice_status === 'cancelled' || invoice.invoice_status === 'voided') && sentDate) {
              label = 'cancellation sent';
              badgeClass = 'badge-dark';
            } else if (sentDate && dueDate && sentDate > dueDate) {
              label = status === 'resent' ? 'resent late' : 'late sent';
              badgeClass = 'badge-warning';
            } else {
              label = status === 'resent' ? 'resent' : 'sent';
              badgeClass = 'badge-success';
            }
            break;
          case 'delivery_confirmed':
            label = 'delivered';
            badgeClass = 'badge-success';
            break;
          case 'delivery_failed':
            label = 'failed';
            badgeClass = 'badge-danger';
            break;
          case 'not_sent':
          default:
            label = 'no';
            badgeClass = 'badge-secondary';
        }

        const attemptsText = (status === 'resent' && typeof (invoice as any).delivery_attempts === 'number' && (invoice as any).delivery_attempts > 1)
          ? ` x${(invoice as any).delivery_attempts}`
          : '';

        const displayDate = deliveredDate || sentDate;
        const dateText = displayDate ? ` (${displayDate.toLocaleDateString()})` : '';

        return (
          <span className={`badge ${badgeClass}`}>{label}{attemptsText}{dateText}</span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, invoice) => (
        <div className="action-buttons">
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginRight: '5px' }}
            onClick={(e) => handleViewInvoice(invoice.invoice_id, e)}
            title="View Invoice"
          >
            👁️
          </button>
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginRight: '5px' }}
            onClick={() => window.open(`/invoices/${invoice.invoice_id}`, '_blank', 'noopener')}
            title="Open in New Tab"
          >
            ↗️
          </button>
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginRight: '5px' }}
            onClick={() => handleEditInvoice(invoice.invoice_id)}
            title="Edit Invoice"
          >
            ✏️
          </button>
          {invoice.invoice_status === 'draft' && (
            <button
              className="btn btn-success btn-sm"
              style={{ marginRight: '5px' }}
              onClick={() => handlePostInvoice(invoice.invoice_id)}
              title="Post Invoice"
            >
              📮
            </button>
          )}
          {invoice.invoice_status === 'finalized' && (invoice.payment_status as string) !== 'paid' && (
            <>
              <button
                className="btn btn-success btn-sm"
                style={{ marginRight: '5px' }}
                onClick={() => handleMarkAsPaid(invoice.invoice_id)}
                title="Mark as Paid"
              >
                💰
              </button>
              <button
                className="btn btn-warning btn-sm"
                style={{ marginRight: '5px' }}
                onClick={() => handleCancelInvoice(invoice.invoice_id)}
                title="Cancel Invoice"
              >
                ❌
              </button>
            </>
          )}
          <ModernInvoicePrintButton
            invoiceId={invoice.invoice_id}
            variant="open"
            className="btn-info btn-sm"
            style={{ marginRight: '5px' }}
            title="View PDF"
          >
            📄
          </ModernInvoicePrintButton>
          {invoice.currency === 'BRL' && (
            <BoletoButton
              invoiceId={invoice.invoice_id}
              className="btn btn-warning btn-sm"
              style={{ marginRight: '5px' }}
            />
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleSendInvoice(invoice.invoice_id)}
            title="Send Email"
          >
            📧
          </button>
          {invoice.currency === 'BRL' && isOverdue(invoice) && (invoice.payment_status as string) !== 'paid' && (
            <button
              className="btn btn-outline-danger btn-sm"
              style={{ marginLeft: '5px' }}
              onClick={() => handleSendLatePayment(invoice.invoice_id)}
              title="Send Late Payment"
            >
              ⚠️
            </button>
          )}
        </div>
      )
    }
  ];

  const isOverdue = (invoice: InvoiceDisplay) => {
    const paymentStatus = invoice.payment_status as string;
    // Do not mark overdue if invoice is voided
    if (invoice.invoice_status === 'voided') {
      return false;
    }
    // Don't mark as overdue if already paid, cancelled, or refunded
    if (
      paymentStatus === 'paid' ||
      paymentStatus === 'processing' ||
      paymentStatus === 'failed' ||
      paymentStatus === 'cancelled' ||
      paymentStatus === 'refunded'
    ) {
      return false;
    }
    const today = new Date();
    const dueDate = invoice.due_date ? new Date(invoice.due_date) : null;
    return !!(dueDate && dueDate < today);
  };

  const getStatusBadgeClass = (status: string) => {
    const statusClasses = {
      draft: 'badge-secondary',
      finalized: 'badge-primary',
      sent: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      cancelled: 'badge-warning',
      voided: 'badge-dark'
    };
    return statusClasses[status as keyof typeof statusClasses] || 'badge-secondary';
  };

  const getPaymentStatusBadgeClass = (status: string) => {
    const statusClasses = {
      not_due: 'badge-secondary',
      processing: 'badge-primary',
      pending: 'badge-warning',
      paid: 'badge-success',
      partial: 'badge-info',
      overdue: 'badge-danger',
      failed: 'badge-danger',
      refunded: 'badge-dark',
      disputed: 'badge-danger',
      written_off: 'badge-dark'
    };
    return statusClasses[status as keyof typeof statusClasses] || 'badge-secondary';
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const isValidEmail = (value: string) => {
    const trimmed = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  const isValidDateRange = (fromDate: string, toDate: string) => {
    if (!fromDate || !toDate) return false;
    return fromDate <= toDate;
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const parseBulkActionResult = (
    action: string,
    response: {
      successful?: any[];
      failed?: any[];
      skipped?: any[];
      errors?: string[];
    }
  ) => {
    const successful = response.successful || [];
    const failed = response.failed || [];
    const skipped = response.skipped || [];
    const errors = response.errors || [];

    const parts: string[] = [];
    if (successful.length > 0) parts.push(`${successful.length} successful`);
    if (failed.length > 0) parts.push(`${failed.length} failed`);
    if (skipped.length > 0) parts.push(`${skipped.length} skipped`);
    if (errors.length > 0) parts.push(`${errors.length} errors`);

    let message = parts.length > 0
      ? `${action} result: ${parts.join(', ')}.`
      : `No result returned while attempting to ${action.toLowerCase()}.`;

    const firstFailure = failed[0]?.error;
    const firstSkipped = skipped[0]?.reason;
    const firstError = errors[0];
    const detail = firstFailure || firstSkipped || firstError;
    if (detail) {
      message = `${message} ${detail}`;
    }

    return {
      ok: successful.length > 0,
      hasIssues: failed.length > 0 || skipped.length > 0 || errors.length > 0,
      message,
    };
  };

  const openReasonModal = (
    action: 'cancel' | 'void',
    invoiceIds: string[],
    targetLabel: string
  ) => {
    setActionFeedback(null);
    setCancelModalAction(action);
    setCancelModalInvoiceIds(invoiceIds);
    setCancelModalTargetLabel(targetLabel);
    setCancelReason('');
    setCancelModalError(null);
    setShowCancelModal(true);
  };

  const fetchInvoicesWithData = async () => {
    try {
      // Build options using centralized service for maintainability
      const options: any = {
        page: 1,
        pageSize: 1000,
        search: searchTerm?.trim() || undefined,
        show_draft: filters.show_draft,
        show_finalized: filters.show_finalized,
        show_cancelled: filters.show_cancelled,
        show_voided: filters.show_voided,
        currencies: selectedCurrencies.length > 0 ? selectedCurrencies : undefined,
      };
      if (paymentFilter === 'paid') options.show_paid = true;
      if (paymentFilter === 'unpaid') options.show_unpaid = true;
      if (paymentFilter === 'overdue') options.show_overdue = true;

      const invoicesData = await apiService.invoices.listInvoices(options);

      // Invoice response already includes customer/contract snapshots.
      const transformedInvoices: InvoiceDisplay[] = (invoicesData || []).map((inv: any) => {
        return {
          ...inv,
          contract_name: inv.contract_name || 'Unknown Contract',
          customer_name: inv.customer_name || 'Unknown Customer'
        };
      });

      setInvoices(transformedInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      await fetchInvoicesWithData();
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceFormMeta = async () => {
    if (contracts.length > 0 && customers.length > 0) {
      return;
    }

    const [contractsResponse, customersResponse] = await Promise.all([
      apiService.getContracts(1, 300).catch(() => ({ items: [] })),
      apiService.getCustomers(1, 300).catch(() => ({ items: [] }))
    ]);

    setContracts(contractsResponse.items || []);
    setCustomers(customersResponse.items || []);
  };

  const fetchContractForInvoice = async (contractId: string) => {
    try {
      await loadInvoiceFormMeta();
      const contract = await apiService.getContract(contractId);
      setSelectedContract(contract);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching contract:', error);
    }
  };

  const handleCreateInvoice = async () => {
    await loadInvoiceFormMeta();
    setSelectedContract(null);
    setShowModal(true);
  };

  const handleSubmitInvoice = async (data: any) => {
    try {
      const currentUser = authService.getUser();
      if (!currentUser) {
        setActionFeedback({ type: 'error', text: 'You must be logged in to create an invoice.' });
        return;
      }
      
      const { invoice_status, sending_status, payment_status, ...cleanData } = data;
      const invoiceData = {
        ...cleanData,
        created_by: currentUser.username || currentUser.email
      };
      
      const response = await apiService.createInvoice(invoiceData);
      
      setShowModal(false);
      navigate('/invoices', { replace: true });
      
      clearInvoiceCache();
      fetchInvoices();
      
      setActionFeedback({ type: 'success', text: `Invoice ${response.invoice_name} created successfully.` });
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create invoice. Please try again.';
      setActionFeedback({ type: 'error', text: `Failed to create invoice: ${errorMessage}` });
    }
  };

  const handleViewInvoice = (invoiceId: string, e?: React.MouseEvent) => {
    // Support Ctrl/Cmd-click to open in a new tab while keeping SPA navigation by default
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(`/invoices/${invoiceId}`, '_blank', 'noopener');
      return;
    }
    navigate(`/invoices/${invoiceId}`);
  };

  const handleEditInvoice = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}?mode=edit`);
  };

  const handleSendInvoice = (invoiceId: string) => {
    const invoice = invoices.find(inv => inv.invoice_id === invoiceId);
    if (!invoice) {
      setActionFeedback({ type: 'error', text: 'Invoice not found.' });
      return;
    }

    const recipientEmail = invoice.customer_email_address;
    if (!recipientEmail) {
      setActionFeedback({
        type: 'error',
        text: 'No email address found for this customer. Please update the customer information first.',
      });
      return;
    }

    setConfirmActionState({
      type: 'send-invoice',
      title: 'Send Invoice Email',
      message: `Send invoice ${invoice.invoice_name || invoice.invoice_number} to ${recipientEmail}?`,
      confirmLabel: 'Send Email',
      invoiceId,
      recipientEmail,
    });
  };

  const executeSendInvoice = async (invoiceId: string, recipientEmail: string) => {
    try {
      const response = await apiService.post(`/api/v1/invoices/${invoiceId}/send-email`, {
        recipient_email: recipientEmail,
        language: null,
        cc_emails: null,
        bcc_emails: null,
        custom_subject: null,
        custom_message: null,
        template_id: null
      }) as { success: boolean; message?: string };

      if (!response.success) {
        throw new Error(response.message || 'Failed to send email');
      }

      setActionFeedback({
        type: 'success',
        text: `Invoice email sent successfully to ${recipientEmail}.`,
      });
      fetchInvoices();
    } catch (error: any) {
      console.error('Error sending invoice email:', error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to send invoice email';
      setActionFeedback({ type: 'error', text: `Failed to send invoice email: ${errorMessage}` });
    }
  };

  const handleSendLatePayment = (invoiceId: string) => {
    const inv = invoices.find(invoiceItem => invoiceItem.invoice_id === invoiceId);
    if (!inv) {
      setActionFeedback({ type: 'error', text: 'Invoice not found.' });
      return;
    }

    setConfirmActionState({
      type: 'send-late-payment',
      title: 'Send Late Payment',
      message: `Send late payment notification with updated boleto for ${inv.invoice_name || inv.invoice_number}?`,
      confirmLabel: 'Send Late Payment',
      invoiceId,
    });
  };

  const executeSendLatePayment = async (invoiceId: string) => {
    try {
      const res = await apiService.boleto.sendUpdatedBoleto(invoiceId);
      if (!res || !(res as any).success) {
        throw new Error((res as any)?.message || 'Failed to send late payment email');
      }

      setActionFeedback({ type: 'success', text: 'Late payment email sent successfully.' });
      fetchInvoices();
    } catch (error: any) {
      console.error('Error sending late payment:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send late payment email';
      setActionFeedback({ type: 'error', text: `Failed to send late payment email: ${errorMessage}` });
    }
  };

  const handlePostInvoice = async (invoiceId: string) => {
    try {
      const currentUser = authService.getUser();
      if (!currentUser) {
        setActionFeedback({ type: 'error', text: 'You must be logged in to post an invoice.' });
        return;
      }
      await apiService.updateInvoiceStatus(invoiceId, {
        invoice_status: 'finalized',
        invoice_updatedby: currentUser.username || currentUser.email
      });

      // After posting, resync upcoming billing periods for the related contract
      const inv = invoices.find(inv => inv.invoice_id === invoiceId);
      if (inv?.contract_id) {
        try {
          await apiService.contracts.syncBillingPeriods(inv.contract_id);
        } catch (e) {
          console.warn('Failed to sync billing periods for contract after posting invoice:', e);
        }
      }
      
      clearInvoiceCache();
      fetchInvoices();
      setActionFeedback({ type: 'success', text: 'Invoice posted successfully.' });
    } catch (error: any) {
      console.error('Error posting invoice:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to post invoice.';
      setActionFeedback({ type: 'error', text: `Failed to post invoice: ${errorMessage}` });
    }
  };

  const handleMarkAsPaid = (invoiceId: string) => {
    const invoice = invoices.find(inv => inv.invoice_id === invoiceId);
    if (!invoice) {
      setActionFeedback({ type: 'error', text: 'Invoice not found.' });
      return;
    }

    setMarkPaidMethod('bank_transfer');
    setMarkPaidState({
      invoiceId,
      invoiceLabel: `${invoice.invoice_name || invoice.invoice_number || 'selected invoice'}`,
    });
  };

  const submitMarkAsPaid = async () => {
    if (!markPaidState) {
      return;
    }
    
    try {
      setMarkPaidLoading(true);
      const currentUser = authService.getUser();
      if (!currentUser) {
        setActionFeedback({ type: 'error', text: 'You must be logged in to update payment status.' });
        return;
      }

      const response = await apiService.put(`/api/v1/invoices/${markPaidState.invoiceId}/payment-status`, {
        payment_status: 'paid',
        payment_date: new Date().toISOString(),
        payment_method: markPaidMethod.trim() || 'bank_transfer',
        payment_reference: `Manual update by ${currentUser.username || currentUser.email}`,
        invoice_updatedby: currentUser.username || currentUser.email
      });

      if (response) {
        setActionFeedback({
          type: 'success',
          text: `Invoice ${markPaidState.invoiceLabel} has been marked as paid.`,
        });
        clearInvoiceCache();
        fetchInvoices();
        setMarkPaidState(null);
      }
    } catch (error: any) {
      console.error('Error marking invoice as paid:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update payment status.';
      setActionFeedback({ type: 'error', text: `Failed to update payment status: ${errorMessage}` });
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const handleCancelInvoice = (invoiceId: string) => {
    const invoice = invoices.find(inv => inv.invoice_id === invoiceId);
    if (!invoice) return;

    openReasonModal(
      'cancel',
      [invoice.invoice_id],
      `${invoice.invoice_name || invoice.invoice_number || 'selected invoice'}`
    );
  };

  // Bulk Actions
  const handleBulkCreateBoletos = async () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to create boletos.' });
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await apiService.post('/api/v1/bulk-invoices/create-boletos', {
        invoice_ids: Array.from(selectedInvoices),
        force_create: false
      }) as {
        successful: any[];
        failed: any[];
        skipped: any[];
      };

      const { successful, failed, skipped } = response;
      
      const message = `Boleto creation result: ${successful.length} successful, ${failed.length} failed, ${skipped.length} skipped.`;
      setActionFeedback({
        type: failed.length > 0 ? 'error' : 'success',
        text: message,
      });
      fetchInvoices();
      setSelectedInvoices(new Set());
    } catch (error: any) {
      console.error('Error creating boletos:', error);
      setActionFeedback({
        type: 'error',
        text: `Failed to create boletos: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkSendUpdatedBoletos = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to send updated boletos.' });
      return;
    }

    setConfirmActionState({
      type: 'bulk-send-updated-boletos',
      title: 'Send Updated Boletos',
      message: `Send updated boletos with late fees for ${selectedInvoices.size} selected invoices?`,
      confirmLabel: 'Send Updated Boletos',
      invoiceIds: Array.from(selectedInvoices),
    });
  };

  const executeBulkSendUpdatedBoletos = async (invoiceIds: string[]) => {
    if (invoiceIds.length === 0) {
      setActionFeedback({ type: 'error', text: 'No invoices were selected for updated boletos.' });
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await apiService.post('/api/v1/bulk-invoices/send-updated-boletos', {
        invoice_ids: invoiceIds
      }) as {
        successful: any[];
        failed: any[];
        skipped: any[];
      };

      const { successful, failed, skipped } = response;
      const message = `Updated boleto email result: ${successful.length} sent, ${failed.length} failed, ${skipped.length} skipped.`;
      setActionFeedback({
        type: failed.length > 0 ? 'error' : 'success',
        text: message,
      });
      fetchInvoices();
      setSelectedInvoices(new Set());
    } catch (error: any) {
      console.error('Error sending updated boletos:', error);
      setActionFeedback({
        type: 'error',
        text: `Failed to send updated boletos: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkSendEmails = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to send emails.' });
      return;
    }

    setConfirmActionState({
      type: 'bulk-send-emails',
      title: 'Send Invoice Emails',
      message: `Send emails for ${selectedInvoices.size} selected invoices?`,
      confirmLabel: 'Send Emails',
      invoiceIds: Array.from(selectedInvoices),
    });
  };

  const executeBulkSendEmails = async (invoiceIds: string[]) => {
    if (invoiceIds.length === 0) {
      setActionFeedback({ type: 'error', text: 'No invoices were selected for email sending.' });
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await apiService.post('/api/v1/bulk-invoices/send-emails', {
        invoice_ids: invoiceIds
      }) as {
        successful: any[];
        failed: any[];
        skipped: any[];
      };

      const { successful, failed, skipped } = response;

      const message = `Email sending result: ${successful.length} sent, ${failed.length} failed, ${skipped.length} skipped.`;
      setActionFeedback({
        type: failed.length > 0 ? 'error' : 'success',
        text: message,
      });
      fetchInvoices();
      setSelectedInvoices(new Set());
    } catch (error: any) {
      console.error('Error sending emails:', error);
      setActionFeedback({
        type: 'error',
        text: `Failed to send emails: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkSendToAccountant = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to send to the accountant.' });
      return;
    }

    const initialCurrencies = [...accountantCurrencyOptions];

    setConfirmActionState({
      type: 'bulk-send-to-accountant',
      title: 'Send Invoices to Accountant',
      message: `Send ${selectedInvoices.size} selected invoices to an accountant email address?`,
      confirmLabel: 'Send to Accountant',
      invoiceIds: Array.from(selectedInvoices),
      accountantEmail: '',
      accountantCurrencies: initialCurrencies,
    });
  };

  const handleBulkSendToAccountantIndividual = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to send to the accountant.' });
      return;
    }

    const initialCurrencies = [...accountantCurrencyOptions];

    setConfirmActionState({
      type: 'bulk-send-to-accountant-individual',
      title: 'Send Invoices to Accountant (Individual Emails)',
      message: `Send ${selectedInvoices.size} selected invoices to an accountant email address with individual emails (runs in background)?`,
      confirmLabel: 'Start Background Task',
      invoiceIds: Array.from(selectedInvoices),
      accountantEmail: '',
      accountantCurrencies: initialCurrencies,
    });
  };

  // Skalar API upload handlers
  const handleBulkUploadToSkalar = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to upload to Skalar.' });
      return;
    }

    const initialCurrencies = [...accountantCurrencyOptions];

    setConfirmActionState({
      type: 'bulk-upload-to-skalar',
      title: 'Upload Invoices to Skalar',
      message: `Upload ${selectedInvoices.size} selected invoices to Skalar accounting system via API?`,
      confirmLabel: 'Upload to Skalar',
      invoiceIds: Array.from(selectedInvoices),
      accountantCurrencies: initialCurrencies,
    });
  };

  const handleBulkUploadToSkalarIndividual = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to upload to Skalar.' });
      return;
    }

    const initialCurrencies = [...accountantCurrencyOptions];

    setConfirmActionState({
      type: 'bulk-upload-to-skalar-individual',
      title: 'Upload Invoices to Skalar (Individual)',
      message: `Upload ${selectedInvoices.size} selected invoices to Skalar accounting system with individual uploads (runs in background)?`,
      confirmLabel: 'Start Background Upload',
      invoiceIds: Array.from(selectedInvoices),
      accountantCurrencies: initialCurrencies,
    });
  };

  const executeBulkSendToAccountant = async (
    invoiceIds: string[],
    accountantEmail: string,
    currencies: string[]
  ) => {
    if (invoiceIds.length === 0) {
      setActionFeedback({ type: 'error', text: 'No invoices were selected for accountant sending.' });
      return;
    }

    if (!isValidEmail(accountantEmail)) {
      setActionFeedback({ type: 'error', text: 'Please provide a valid accountant email address.' });
      return;
    }

    if (currencies.length === 0) {
      setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await apiService.post('/api/v1/bulk-invoices/send-to-accountant', {
        invoice_ids: invoiceIds,
        accountant_email: accountantEmail.trim(),
        currencies
      }) as {
        successful?: any[];
        failed?: any[];
        errors?: string[];
        email_sent?: boolean;
      };

      const successful = response.successful || [];
      const failed = response.failed || [];
      const errors = response.errors || [];
      const emailSent = !!response.email_sent;

      const message = `Accountant sending result: ${successful.length} attached, ${failed.length} failed.${emailSent ? ` Email sent to ${accountantEmail.trim()}.` : ' Email was not sent.'}`;
      setActionFeedback({
        type: emailSent && failed.length === 0 && errors.length === 0 ? 'success' : 'error',
        text: errors.length > 0 ? `${message} ${errors[0]}` : message,
      });

      if (emailSent) {
        setSelectedInvoices(new Set());
      }
    } catch (error: any) {
      console.error('Error sending invoices to accountant:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setActionFeedback({
        type: 'error',
        text: `Failed to send invoices to accountant: ${errorMessage}`,
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const checkAccountantSendTaskStatus = async () => {
    if (!accountantSendTaskId) return;
    
    try {
      const response = await apiService.get(`/api/v1/tasks/status/${accountantSendTaskId}`) as {
        status: string;
        result?: any;
        error?: string;
        progress?: {
          processed: number;
          total: number;
          current_item?: string;
          current_invoice_id?: string;
          status?: string;
        };
        message?: string;
      };
      
      if (response.status === 'SUCCESS') {
        setAccountantSendStatus('success');
        if (response.result) {
          const result = response.result;
          const successful = result.successful?.length || 0;
          const failed = result.failed?.length || 0;
          const skipped = result.skipped?.length || 0;
          const emailsSent = result.emails_sent || 0;
          setAccountantSendSummary(`Completed: ${successful} sent, ${failed} failed, ${skipped} skipped. Emails sent: ${emailsSent}`);
        }
      } else if (response.status === 'FAILURE') {
        setAccountantSendStatus('failure');
        setAccountantSendError(response.error || response.message || 'Unknown error');
      } else if (response.status === 'STARTED' || response.status === 'PENDING') {
        setAccountantSendStatus('running');
        if (response.progress) {
          setAccountantSendProgress({
            processed: response.progress.processed || 0,
            total: response.progress.total || 0,
            current_item: response.progress.current_invoice_id || response.progress.current_item,
            status: response.progress.status
          });
        }
      } else {
        setAccountantSendStatus(response.status as any);
      }
    } catch (error: any) {
      console.error('Error checking accountant send task status:', error);
      setAccountantSendStatus('failure');
      setAccountantSendError('Failed to check task status');
    }
  };

  // Skalar upload task status check
  const checkSkalarUploadTaskStatus = async () => {
    if (!skalarUploadTaskId) return;
    
    try {
      const response = await apiService.get(`/api/v1/tasks/status/${skalarUploadTaskId}`) as {
        status: string;
        result?: any;
        error?: string;
        progress?: {
          processed: number;
          total: number;
          current_item?: string;
          current_invoice_id?: string;
          status?: string;
        };
        message?: string;
      };
      
      if (response.status === 'SUCCESS') {
        setSkalarUploadStatus('success');
        if (response.result) {
          const result = response.result;
          const uploaded = result.uploaded || result.successful?.length || 0;
          const failed = result.failed?.length || 0;
          const skipped = result.skipped?.length || 0;
          setSkalarUploadSummary(`Completed: ${uploaded} uploaded, ${failed} failed, ${skipped} skipped`);
        }
      } else if (response.status === 'FAILURE') {
        setSkalarUploadStatus('failure');
        setSkalarUploadError(response.error || response.message || 'Unknown error');
      } else if (response.status === 'STARTED' || response.status === 'PENDING') {
        setSkalarUploadStatus('running');
        if (response.progress) {
          setSkalarUploadProgress({
            processed: response.progress.processed || 0,
            total: response.progress.total || 0,
            current_item: response.progress.current_invoice_id || response.progress.current_item,
            status: response.progress.status
          });
        }
      } else {
        setSkalarUploadStatus(response.status as any);
      }
    } catch (error: any) {
      console.error('Error checking Skalar upload task status:', error);
      setSkalarUploadStatus('failure');
      setSkalarUploadError('Failed to check task status');
    }
  };

  // Set up polling for Skalar upload task status
  useEffect(() => {
    if (!showSkalarUploadModal || !skalarUploadTaskId || (skalarUploadStatus !== 'starting' && skalarUploadStatus !== 'running')) {
      return;
    }

    // Initial check
    checkSkalarUploadTaskStatus();

    const interval = setInterval(() => {
      checkSkalarUploadTaskStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [showSkalarUploadModal, skalarUploadTaskId, skalarUploadStatus]);

  const executeBulkSendToAccountantIndividual = async (
    invoiceIds: string[],
    accountantEmail: string,
    currencies: string[]
  ) => {
    if (invoiceIds.length === 0) {
      setActionFeedback({ type: 'error', text: 'No invoices were selected for accountant sending.' });
      return;
    }

    if (!isValidEmail(accountantEmail)) {
      setActionFeedback({ type: 'error', text: 'Please provide a valid accountant email address.' });
      return;
    }

    if (currencies.length === 0) {
      setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
      return;
    }

    setAccountantSendStatus('starting');
    setAccountantSendError(null);
    setAccountantSendSummary(null);
    setAccountantSendProgress(null);

    try {
      const response = await apiService.post('/api/v1/bulk-invoices/send-to-accountant-individual', {
        invoice_ids: invoiceIds,
        accountant_email: accountantEmail.trim(),
        currencies
      }) as {
        task_id: string;
        status: string;
        message: string;
        status_url: string;
        total_invoices: number;
        accountant_email: string;
      };

      setAccountantSendTaskId(response.task_id);
      setAccountantSendStatus('running');
      setAccountantSendProgress({
        processed: 0,
        total: response.total_invoices || invoiceIds.length,
        status: 'starting'
      });
      setShowAccountantSendModal(true);
      
      // Show success message that background task started
      setActionFeedback({
        type: 'success', 
        text: `Accountant email sending started in background for ${invoiceIds.length} invoices`
      });
      
      // Clear selection after starting the background task
      setSelectedInvoices(new Set());
      
    } catch (error: any) {
      console.error('Error starting accountant send task:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setAccountantSendStatus('failure');
      setAccountantSendError(errorMessage);
      setActionFeedback({
        type: 'error',
        text: `Failed to start accountant email sending: ${errorMessage}`,
      });
    }
  };

  // Skalar API upload execution functions
  const executeBulkUploadToSkalar = async (
    invoiceIds: string[],
    currencies: string[]
  ) => {
    if (invoiceIds.length === 0) {
      setActionFeedback({ type: 'error', text: 'No invoices were selected for Skalar upload.' });
      return;
    }

    if (currencies.length === 0) {
      setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
      return;
    }

    setSkalarUploadStatus('starting');
    setSkalarUploadError(null);
    setSkalarUploadSummary(null);
    setSkalarUploadProgress(null);

    try {
      const response = await apiService.post('/api/v1/bulk-invoices/upload-to-skalar', {
        invoice_ids: invoiceIds,
        currencies
      }) as {
        task_id: string;
        status: string;
        message: string;
        status_url: string;
        total_invoices: number;
      };

      setSkalarUploadTaskId(response.task_id);
      setSkalarUploadStatus('running');
      setSkalarUploadProgress({
        processed: 0,
        total: response.total_invoices || invoiceIds.length,
        status: 'starting'
      });
      setShowSkalarUploadModal(true);
      
      // Show success message that background task started
      setActionFeedback({
        type: 'success', 
        text: `Skalar upload started in background for ${invoiceIds.length} invoices`
      });
      
      // Clear selection after starting the background task
      setSelectedInvoices(new Set());
      
    } catch (error: any) {
      console.error('Error starting Skalar upload task:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setSkalarUploadStatus('failure');
      setSkalarUploadError(errorMessage);
      setActionFeedback({
        type: 'error',
        text: `Failed to start Skalar upload: ${errorMessage}`,
      });
    }
  };

  const executeBulkUploadToSkalarIndividual = async (
    invoiceIds: string[],
    currencies: string[]
  ) => {
    if (invoiceIds.length === 0) {
      setActionFeedback({ type: 'error', text: 'No invoices were selected for Skalar upload.' });
      return;
    }

    if (currencies.length === 0) {
      setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
      return;
    }

    setSkalarUploadStatus('starting');
    setSkalarUploadError(null);
    setSkalarUploadSummary(null);
    setSkalarUploadProgress(null);

    try {
      const response = await apiService.post('/api/v1/bulk-invoices/upload-to-skalar', {
        invoice_ids: invoiceIds,
        currencies,
        use_individual_uploads: true
      }) as {
        task_id: string;
        status: string;
        message: string;
        status_url: string;
        total_invoices: number;
      };

      setSkalarUploadTaskId(response.task_id);
      setSkalarUploadStatus('running');
      setSkalarUploadProgress({
        processed: 0,
        total: response.total_invoices || invoiceIds.length,
        status: 'starting'
      });
      setShowSkalarUploadModal(true);
      
      // Show success message that background task started
      setActionFeedback({
        type: 'success', 
        text: `Skalar individual upload started in background for ${invoiceIds.length} invoices`
      });
      
      // Clear selection after starting the background task
      setSelectedInvoices(new Set());
      
    } catch (error: any) {
      console.error('Error starting Skalar individual upload task:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setSkalarUploadStatus('failure');
      setSkalarUploadError(errorMessage);
      setActionFeedback({
        type: 'error',
        text: `Failed to start Skalar upload: ${errorMessage}`,
      });
    }
  };

  const handleBulkPost = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to post.' });
      return;
    }

    setConfirmActionState({
      type: 'bulk-post',
      title: 'Post Invoices',
      message: `Post (finalize) ${selectedInvoices.size} selected invoice(s)?`,
      confirmLabel: 'Post Invoices',
      invoiceIds: Array.from(selectedInvoices),
    });
  };

  const executeBulkPost = async (invoiceIds: string[]) => {
    if (invoiceIds.length === 0) {
      setActionFeedback({ type: 'error', text: 'No invoices were selected for posting.' });
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await apiService.post('/api/v1/bulk-invoices/post', {
        invoice_ids: invoiceIds
      }) as { successful: any[]; failed: any[]; skipped: any[] };
      const { successful, failed, skipped } = response;
      const msg = `Post result: ${successful.length} posted, ${failed.length} failed, ${skipped.length} skipped.`;
      setActionFeedback({
        type: failed.length > 0 ? 'error' : 'success',
        text: msg,
      });

      // After bulk posting, resync all contracts upcoming billing periods to reflect changes
      try {
        await apiService.contracts.syncBillingPeriods();
      } catch (e) {
        console.warn('Failed to sync billing periods after bulk post:', e);
      }

      clearInvoiceCache();
      fetchInvoices();
      setSelectedInvoices(new Set());
    } catch (error: any) {
      console.error('Error posting invoices:', error);
      setActionFeedback({
        type: 'error',
        text: `Failed to post invoices: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkVoid = () => {
    if (selectedInvoices.size === 0) {
      setActionFeedback({ type: 'error', text: 'Please select invoices to void.' });
      return;
    }

    openReasonModal(
      'void',
      Array.from(selectedInvoices),
      `${selectedInvoices.size} selected invoice(s)`
    );
  };

  const submitCancelOrVoid = async () => {
    if (cancelModalInvoiceIds.length === 0) {
      setCancelModalError('No invoices were selected.');
      return;
    }

    if (!cancelReason.trim()) {
      setCancelModalError(`Please provide a reason to ${cancelModalAction}.`);
      return;
    }

    setCancelModalError(null);
    setBulkProcessing(true);
    try {
      const endpoint =
        cancelModalAction === 'void'
          ? '/api/v1/bulk-invoices/void'
          : '/api/v1/bulk-invoices/cancel';
      const response = await apiService.post(endpoint, {
        invoice_ids: cancelModalInvoiceIds,
        reason: cancelReason
      }) as {
        successful?: any[];
        failed?: any[];
        skipped?: any[];
        errors?: string[];
      };

      const actionLabel = cancelModalAction === 'void' ? 'Void' : 'Cancel';
      const outcome = parseBulkActionResult(actionLabel, response);
      setActionFeedback({
        type: outcome.ok && !outcome.hasIssues ? 'success' : 'error',
        text: outcome.message,
      });

      if (outcome.ok) {
        clearInvoiceCache();
        await fetchInvoices();
        if (cancelModalInvoiceIds.length > 1) {
          setSelectedInvoices(new Set());
        }
        setShowCancelModal(false);
        setCancelReason('');
        setCancelModalInvoiceIds([]);
        setCancelModalTargetLabel('');
      }
    } catch (error: any) {
      console.error('Error cancelling invoices:', error);
      const action = cancelModalAction === 'void' ? 'void' : 'cancel';
      setCancelModalError(`Failed to ${action} invoices: ${error.message || 'Unknown error'}`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmActionState) {
      return;
    }

    setConfirmActionLoading(true);
    try {
      if (confirmActionState.type === 'send-invoice') {
        if (!confirmActionState.invoiceId || !confirmActionState.recipientEmail) {
          setActionFeedback({ type: 'error', text: 'Invoice email action is missing required data.' });
          return;
        }
        await executeSendInvoice(confirmActionState.invoiceId, confirmActionState.recipientEmail);
      } else if (confirmActionState.type === 'send-late-payment') {
        if (!confirmActionState.invoiceId) {
          setActionFeedback({ type: 'error', text: 'Late payment action is missing invoice information.' });
          return;
        }
        await executeSendLatePayment(confirmActionState.invoiceId);
      } else if (confirmActionState.type === 'bulk-send-updated-boletos') {
        await executeBulkSendUpdatedBoletos(confirmActionState.invoiceIds || []);
      } else if (confirmActionState.type === 'bulk-send-emails') {
        await executeBulkSendEmails(confirmActionState.invoiceIds || []);
      } else if (confirmActionState.type === 'bulk-send-to-accountant') {
        const accountantEmail = (confirmActionState.accountantEmail || '').trim();
        const accountantCurrencies = (confirmActionState.accountantCurrencies || []).map((currency) => currency.toUpperCase());
        if (!accountantEmail || !isValidEmail(accountantEmail)) {
          setActionFeedback({ type: 'error', text: 'Please provide a valid accountant email address.' });
          return;
        }
        if (accountantCurrencies.length === 0) {
          setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
          return;
        }
        await executeBulkSendToAccountant(confirmActionState.invoiceIds || [], accountantEmail, accountantCurrencies);
      } else if (confirmActionState.type === 'bulk-send-to-accountant-individual') {
        const accountantEmail = (confirmActionState.accountantEmail || '').trim();
        const accountantCurrencies = (confirmActionState.accountantCurrencies || []).map((currency) => currency.toUpperCase());
        if (!accountantEmail || !isValidEmail(accountantEmail)) {
          setActionFeedback({ type: 'error', text: 'Please provide a valid accountant email address.' });
          return;
        }
        if (accountantCurrencies.length === 0) {
          setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
          return;
        }
        await executeBulkSendToAccountantIndividual(confirmActionState.invoiceIds || [], accountantEmail, accountantCurrencies);
      } else if (confirmActionState.type === 'bulk-upload-to-skalar') {
        const skalarCurrencies = (confirmActionState.accountantCurrencies || []).map((currency) => currency.toUpperCase());
        if (skalarCurrencies.length === 0) {
          setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
          return;
        }
        await executeBulkUploadToSkalar(confirmActionState.invoiceIds || [], skalarCurrencies);
      } else if (confirmActionState.type === 'bulk-upload-to-skalar-individual') {
        const skalarCurrencies = (confirmActionState.accountantCurrencies || []).map((currency) => currency.toUpperCase());
        if (skalarCurrencies.length === 0) {
          setActionFeedback({ type: 'error', text: 'Please select at least one currency.' });
          return;
        }
        await executeBulkUploadToSkalarIndividual(confirmActionState.invoiceIds || [], skalarCurrencies);
      } else if (confirmActionState.type === 'bulk-post') {
        await executeBulkPost(confirmActionState.invoiceIds || []);
      }

      setConfirmActionState(null);
    } finally {
      setConfirmActionLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await apiService.post('/api/v1/bulk-invoices/check-payments', {}) as {
        bank_transfers: {
          updated_invoices: number;
          matched_transfers: any[];
        };
        boletos: {
          updated_invoices: number;
          checked_boletos: any[];
        };
        overdue: {
          marked_overdue: any[];
        };
      };
      
      const { bank_transfers, boletos, overdue } = response;

      // Refresh invoices if any were updated
      if (bank_transfers.updated_invoices > 0 || 
          boletos.updated_invoices > 0 || 
          (overdue.marked_overdue && overdue.marked_overdue.length > 0)) {
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const openDateRangeExportModal = () => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateRangeExportFromDate(monthStart.toISOString().slice(0, 10));
    setDateRangeExportToDate(now.toISOString().slice(0, 10));
    setDateRangeExportTaskId(null);
    setDateRangeExportStatus('idle');
    setDateRangeExportError(null);
    setDateRangeExportSummary(null);
    setDateRangeExportDownloading(false);
    setDateRangeExportCurrencies([...AVAILABLE_CURRENCIES]);
    setShowDateRangeExportModal(true);
  };

  const startDateRangeExport = async () => {
    if (!isValidDateRange(dateRangeExportFromDate, dateRangeExportToDate)) {
      setDateRangeExportError('Please choose a valid date range (from date must be before or equal to to date).');
      return;
    }

    if (dateRangeExportCurrencies.length === 0) {
      setDateRangeExportError('Please select at least one currency.');
      return;
    }

    try {
      setDateRangeExportStatus('starting');
      setDateRangeExportError(null);
      setDateRangeExportSummary(null);

      const response = await apiService.invoices.startInvoiceDateRangeExport(
        dateRangeExportFromDate,
        dateRangeExportToDate,
        dateRangeExportCurrencies
      );

      setDateRangeExportTaskId(response.task_id);
      setDateRangeExportStatus('running');
    } catch (error: any) {
      console.error('Error starting invoice ZIP export:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to start export job.';
      setDateRangeExportStatus('failure');
      setDateRangeExportError(errorMessage);
    }
  };

  const downloadDateRangeExport = async () => {
    if (!dateRangeExportTaskId) {
      setDateRangeExportError('No export job available for download.');
      return;
    }

    try {
      setDateRangeExportDownloading(true);
      setDateRangeExportError(null);
      await apiService.invoices.downloadInvoiceDateRangeExport(dateRangeExportTaskId);
    } catch (error: any) {
      console.error('Error downloading invoice ZIP export:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to download ZIP export.';
      setDateRangeExportError(errorMessage);
    } finally {
      setDateRangeExportDownloading(false);
    }
  };

  useEffect(() => {
    if (!dateRangeExportTaskId || dateRangeExportStatus !== 'running') {
      return;
    }

    let cancelled = false;

    const pollStatus = async () => {
      try {
        const statusResponse = await apiService.invoices.getInvoiceDateRangeExportStatus(dateRangeExportTaskId);
        if (cancelled) {
          return;
        }

        if (statusResponse.status === 'SUCCESS') {
          const result = statusResponse.result || {};
          const summaryParts: string[] = [];
          summaryParts.push(`Exported ${result.exported_count ?? 0} of ${result.total_invoices ?? 0} invoice(s)`);
          if (typeof result.failed_count === 'number' && result.failed_count > 0) {
            summaryParts.push(`${result.failed_count} failed`);
          }
          if (result.zip_size_bytes) {
            summaryParts.push(`ZIP size ${formatBytes(result.zip_size_bytes)}`);
          }
          setDateRangeExportSummary(summaryParts.join(' | '));
          setDateRangeExportStatus('success');
          setDateRangeExportError(null);
          return;
        }

        if (statusResponse.status === 'FAILURE' || statusResponse.status === 'CANCELED') {
          setDateRangeExportStatus('failure');
          setDateRangeExportError(statusResponse.error || 'Export job failed.');
        }
      } catch (error: any) {
        if (cancelled) {
          return;
        }
        console.error('Error polling invoice ZIP export status:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to poll export status.';
        setDateRangeExportStatus('failure');
        setDateRangeExportError(errorMessage);
      }
    };

    pollStatus();
    const intervalId = window.setInterval(pollStatus, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [dateRangeExportTaskId, dateRangeExportStatus]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Invoices</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={handleCreateInvoice}>
            + Create Invoice
          </button>
          <button className="btn btn-info" onClick={checkPaymentStatus}>
            🔄 Check Payments
          </button>
          <button className="btn btn-secondary" onClick={openDateRangeExportModal}>
            📦 Export ZIP
          </button>
        </div>
      </div>

      {actionFeedback && (
        <div
          className={actionFeedback.type === 'success' ? 'alert alert-success' : 'alert alert-danger'}
          style={{ marginBottom: '16px' }}
        >
          {actionFeedback.text}
        </div>
      )}

      {selectedInvoices.size > 0 && (
        <div className="bulk-actions-bar" style={{
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          marginBottom: '20px',
          display: 'flex',
          gap: '10px',
          alignItems: 'center'
        }}>
          <span>{selectedInvoices.size} invoice(s) selected</span>
          <button 
            className="btn btn-warning btn-sm" 
            onClick={handleBulkCreateBoletos}
            disabled={bulkProcessing}
          >
            Create Boletos
          </button>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={handleBulkSendEmails}
            disabled={bulkProcessing}
          >
            Send Emails
          </button>
          <button
            className="btn btn-info btn-sm"
            onClick={handleBulkSendToAccountant}
            disabled={bulkProcessing}
            title="Send selected invoices to an accountant email with PDF attachments"
          >
            Send to Accountant
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleBulkSendToAccountantIndividual}
            disabled={bulkProcessing}
            title="Send selected invoices to an accountant with individual emails (background task)"
          >
            Send to Accountant (Individual)
          </button>
          <button
            className="btn btn-dark btn-sm"
            onClick={handleBulkUploadToSkalar}
            disabled={bulkProcessing}
            title="Upload selected invoices to Skalar accounting system via API"
          >
            Upload to Skalar
          </button>
          <button
            className="btn btn-warning btn-sm"
            onClick={handleBulkUploadToSkalarIndividual}
            disabled={bulkProcessing}
            title="Upload selected invoices to Skalar with individual uploads (background task)"
          >
            Upload to Skalar (Individual)
          </button>
          <button 
            className="btn btn-success btn-sm" 
            onClick={handleBulkSendUpdatedBoletos}
            disabled={bulkProcessing}
            title="Send updated boletos with late fees"
          >
            Send Updated Boletos
          </button>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={handleBulkPost}
            disabled={bulkProcessing}
            title="Post (finalize) selected invoices"
          >
            Post
          </button>
          <button 
            className="btn btn-dark btn-sm" 
            onClick={handleBulkVoid}
            disabled={bulkProcessing}
            title="Void selected invoices"
          >
            Void
          </button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => openReasonModal(
              'cancel',
              Array.from(selectedInvoices),
              `${selectedInvoices.size} selected invoice(s)`
            )}
            disabled={bulkProcessing}
          >
            Cancel Invoices
          </button>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={() => setSelectedInvoices(new Set())}
          >
            Clear Selection
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <SearchInput
              onSearch={setSearchTerm}
              placeholder="Search invoices..."
              className="flex-1"
            />
            <div>
              <span className="badge badge-success">Paid</span>
              <span className="badge badge-danger" style={{ marginLeft: '5px' }}>Overdue</span>
              <span className="badge badge-warning" style={{ marginLeft: '5px' }}>Pending</span>
            </div>
          </div>
          <div className="filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <strong>Payment:</strong>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value as any)}
                className="form-control"
                style={{ width: 160 }}
              >
                <option value="any">Any</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <label><input type="checkbox" checked={filters.show_draft} onChange={(e) => setFilters(prev => ({ ...prev, show_draft: e.target.checked }))} /> Draft</label>
            <label><input type="checkbox" checked={filters.show_finalized} onChange={(e) => setFilters(prev => ({ ...prev, show_finalized: e.target.checked }))} /> Finalized</label>
            <label><input type="checkbox" checked={filters.show_cancelled} onChange={(e) => setFilters(prev => ({ ...prev, show_cancelled: e.target.checked }))} /> Cancelled</label>
            <label><input type="checkbox" checked={filters.show_voided} onChange={(e) => setFilters(prev => ({ ...prev, show_voided: e.target.checked }))} /> Voided</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <strong>Currency:</strong>
              {Array.from(AVAILABLE_CURRENCIES).map((cur) => (
                <label key={cur}>
                  <input
                    type="checkbox"
                    checked={selectedCurrencies.includes(cur)}
                    onChange={(e) => {
                      setSelectedCurrencies((prev) => {
                        if (e.target.checked) return [...prev, cur];
                        return prev.filter((c) => c !== cur);
                      });
                    }}
                  /> {cur}
                </label>
              ))}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
              <button className="btn btn-primary btn-sm" onClick={applyFilters}>Apply Filters</button>
              <button className="btn btn-secondary btn-sm" onClick={clearFiltersUI}>Clear</button>
            </div>
          </div>
        </div>

        <SortableTable
          data={filteredInvoices}
          columns={tableColumns}
          rowKey="invoice_id"
          loading={loading}
          emptyMessage="No invoices found. Create your first invoice by clicking the button above or from a contract."
          rowStyle={getRowStyle}
        />
      </div>

      {showModal && (
        <Modal
          title={selectedContract ? `Create Invoice for ${selectedContract.contract_name}` : 'Create Invoice'}
          onClose={() => {
            setShowModal(false);
            if (location.state?.contractId) {
              navigate('/invoices', { replace: true });
            }
          }}
        >
          <InvoiceFormEnhanced
            contract={selectedContract}
            contracts={contracts}
            customers={customers}
            onSubmit={handleSubmitInvoice}
            onCancel={() => {
              setShowModal(false);
              if (location.state?.contractId) {
                navigate('/invoices', { replace: true });
              }
            }}
          />
        </Modal>
      )}

      {showDateRangeExportModal && (
        <Modal
          title="Export Invoices by Date Range"
          onClose={() => {
            if (dateRangeExportStatus === 'starting' || dateRangeExportStatus === 'running' || dateRangeExportDownloading) {
              return;
            }
            setShowDateRangeExportModal(false);
          }}
        >
          <div>
            <p style={{ marginBottom: '12px' }}>
              Create a ZIP export of invoices by <strong>invoice date</strong>. The ZIP contains one folder per currency.
            </p>
            <div className="form-group">
              <label>From Date *</label>
              <input
                type="date"
                className="form-control"
                value={dateRangeExportFromDate}
                onChange={(e) => setDateRangeExportFromDate(e.target.value)}
                disabled={dateRangeExportStatus === 'starting' || dateRangeExportStatus === 'running' || dateRangeExportDownloading}
              />
            </div>
            <div className="form-group" style={{ marginTop: '10px' }}>
              <label>To Date *</label>
              <input
                type="date"
                className="form-control"
                value={dateRangeExportToDate}
                onChange={(e) => setDateRangeExportToDate(e.target.value)}
                disabled={dateRangeExportStatus === 'starting' || dateRangeExportStatus === 'running' || dateRangeExportDownloading}
              />
            </div>
            <div className="form-group" style={{ marginTop: '10px' }}>
              <label>Currencies *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '6px' }}>
                {Array.from(AVAILABLE_CURRENCIES).map((currency) => (
                  <label key={`export-${currency}`} style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={dateRangeExportCurrencies.includes(currency)}
                      onChange={(e) =>
                        setDateRangeExportCurrencies((prev) => {
                          if (e.target.checked) return [...prev, currency];
                          return prev.filter((value) => value !== currency);
                        })
                      }
                      disabled={dateRangeExportStatus === 'starting' || dateRangeExportStatus === 'running' || dateRangeExportDownloading}
                    />{' '}
                    {currency}
                  </label>
                ))}
              </div>
              {dateRangeExportCurrencies.length === 0 && (
                <small style={{ color: '#dc3545' }}>Select at least one currency.</small>
              )}
            </div>

            {!isValidDateRange(dateRangeExportFromDate, dateRangeExportToDate) && (
              <div className="alert alert-danger" style={{ marginTop: '12px', marginBottom: 0 }}>
                Please choose a valid date range.
              </div>
            )}

            {dateRangeExportStatus === 'running' && (
              <div className="alert alert-info" style={{ marginTop: '12px', marginBottom: 0 }}>
                Export job is running in the background. This can take a while for large date ranges.
              </div>
            )}

            {dateRangeExportSummary && (
              <div className="alert alert-success" style={{ marginTop: '12px', marginBottom: 0 }}>
                {dateRangeExportSummary}
              </div>
            )}

            {dateRangeExportError && (
              <div className="alert alert-danger" style={{ marginTop: '12px', marginBottom: 0 }}>
                {dateRangeExportError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDateRangeExportModal(false)}
                disabled={dateRangeExportStatus === 'starting' || dateRangeExportStatus === 'running' || dateRangeExportDownloading}
              >
                Close
              </button>
              {dateRangeExportStatus === 'success' && (
                <button
                  className="btn btn-success"
                  onClick={downloadDateRangeExport}
                  disabled={dateRangeExportDownloading}
                >
                  {dateRangeExportDownloading ? 'Downloading...' : 'Download ZIP'}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={startDateRangeExport}
                disabled={
                  dateRangeExportStatus === 'starting' ||
                  dateRangeExportStatus === 'running' ||
                  dateRangeExportDownloading ||
                  !isValidDateRange(dateRangeExportFromDate, dateRangeExportToDate) ||
                  dateRangeExportCurrencies.length === 0
                }
              >
                {dateRangeExportStatus === 'starting'
                  ? 'Starting...'
                  : dateRangeExportStatus === 'running'
                    ? 'Export Running...'
                    : 'Start Export'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showAccountantSendModal && (
        <Modal
          title="Accountant Email Sending Progress"
          onClose={() => {
            if (accountantSendStatus === 'starting' || accountantSendStatus === 'running') {
              return; // Don't allow closing while running
            }
            setShowAccountantSendModal(false);
            setAccountantSendTaskId(null);
            setAccountantSendStatus('idle');
            setAccountantSendProgress(null);
          }}
        >
          <div>
            <p style={{ marginBottom: '12px' }}>
              Sending invoices to accountant with individual emails in the background.
            </p>
            
            {accountantSendStatus === 'running' && accountantSendProgress && (
              <div>
                <div className="progress" style={{ marginBottom: '10px', height: '20px' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: `${(accountantSendProgress.processed / accountantSendProgress.total) * 100}%` }}
                    aria-valuenow={(accountantSendProgress.processed / accountantSendProgress.total) * 100}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    {accountantSendProgress.processed} of {accountantSendProgress.total} ({Math.round((accountantSendProgress.processed / accountantSendProgress.total) * 100)}%)
                  </div>
                </div>
                <p>
                  <strong>Progress:</strong> {accountantSendProgress.processed} of {accountantSendProgress.total} invoices processed
                </p>
                {accountantSendProgress.current_item && (
                  <p>
                    <strong>Current:</strong> {accountantSendProgress.current_item}
                  </p>
                )}
                {accountantSendProgress.status && (
                  <p>
                    <strong>Status:</strong> {accountantSendProgress.status}
                  </p>
                )}
              </div>
            )}
            
            {accountantSendStatus === 'success' && accountantSendSummary && (
              <div className="alert alert-success" style={{ marginTop: '12px', marginBottom: 0 }}>
                {accountantSendSummary}
              </div>
            )}
            
            {accountantSendStatus === 'failure' && accountantSendError && (
              <div className="alert alert-danger" style={{ marginTop: '12px', marginBottom: 0 }}>
                {accountantSendError}
              </div>
            )}
            
            {accountantSendStatus === 'starting' && (
              <div className="alert alert-info" style={{ marginTop: '12px', marginBottom: 0 }}>
                Starting background task...
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (accountantSendStatus === 'starting' || accountantSendStatus === 'running') {
                    return; // Don't allow closing while running
                  }
                  setShowAccountantSendModal(false);
                  setAccountantSendTaskId(null);
                  setAccountantSendStatus('idle');
                  setAccountantSendProgress(null);
                }}
                disabled={accountantSendStatus === 'starting' || accountantSendStatus === 'running'}
              >
                Close
              </button>
              {(accountantSendStatus === 'starting' || accountantSendStatus === 'running') && (
                <button
                  className="btn btn-info"
                  onClick={checkAccountantSendTaskStatus}
                  disabled={!accountantSendTaskId}
                >
                  Refresh Status
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Skalar Upload Progress Modal */}
      {showSkalarUploadModal && (
        <Modal
          title="Skalar Upload Progress"
          onClose={() => {
            if (skalarUploadStatus === 'starting' || skalarUploadStatus === 'running') {
              return; // Don't allow closing while running
            }
            setShowSkalarUploadModal(false);
            setSkalarUploadTaskId(null);
            setSkalarUploadStatus('idle');
            setSkalarUploadProgress(null);
          }}
        >
          <div>
            <p style={{ marginBottom: '12px' }}>
              Uploading invoices to Skalar accounting system via API.
            </p>
            
            {skalarUploadStatus === 'running' && skalarUploadProgress && (
              <div>
                <div className="progress" style={{ marginBottom: '10px', height: '20px' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: `${(skalarUploadProgress.processed / skalarUploadProgress.total) * 100}%` }}
                    aria-valuenow={(skalarUploadProgress.processed / skalarUploadProgress.total) * 100}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
                <p style={{ marginBottom: '10px' }}>
                  <strong>Progress:</strong> {skalarUploadProgress.processed} of {skalarUploadProgress.total} ({Math.round((skalarUploadProgress.processed / skalarUploadProgress.total) * 100)}%)
                </p>
                {skalarUploadProgress.current_invoice_id && (
                  <p style={{ marginBottom: '10px', fontSize: '0.9em', color: '#666' }}>
                    Currently processing: {skalarUploadProgress.current_invoice_id}
                  </p>
                )}
              </div>
            )}
            
            {skalarUploadStatus === 'success' && skalarUploadSummary && (
              <div className="alert alert-success" style={{ marginTop: '12px', marginBottom: 0 }}>
                Upload completed successfully!
                <br />
                {skalarUploadSummary}
              </div>
            )}
            
            {skalarUploadStatus === 'failure' && skalarUploadError && (
              <div className="alert alert-danger" style={{ marginTop: '12px', marginBottom: 0 }}>
                {skalarUploadError}
              </div>
            )}
            
            {skalarUploadStatus === 'starting' && (
              <div className="alert alert-info" style={{ marginTop: '12px', marginBottom: 0 }}>
                Starting Skalar upload task...
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (skalarUploadStatus === 'starting' || skalarUploadStatus === 'running') {
                    return; // Don't allow closing while running
                  }
                  setShowSkalarUploadModal(false);
                  setSkalarUploadTaskId(null);
                  setSkalarUploadStatus('idle');
                  setSkalarUploadProgress(null);
                }}
                disabled={skalarUploadStatus === 'starting' || skalarUploadStatus === 'running'}
              >
                Close
              </button>
              {(skalarUploadStatus === 'starting' || skalarUploadStatus === 'running') && (
                <button
                  className="btn btn-info"
                  onClick={checkSkalarUploadTaskStatus}
                  disabled={!skalarUploadTaskId}
                >
                  Refresh Status
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {showCancelModal && (
        <Modal
          title={cancelModalAction === 'void' ? 'Void Invoices' : 'Cancel Invoices'}
          onClose={() => {
            if (bulkProcessing) {
              return;
            }
            setShowCancelModal(false);
            setCancelReason('');
            setCancelModalError(null);
            setCancelModalInvoiceIds([]);
            setCancelModalTargetLabel('');
          }}
        >
          <div>
            <p>
              You are about to {cancelModalAction}{' '}
              <strong>{cancelModalTargetLabel || `${cancelModalInvoiceIds.length} invoice(s)`}</strong>.
            </p>
            <div className="form-group">
              <label>{cancelModalAction === 'void' ? 'Void Reason *' : 'Cancellation Reason *'}</label>
              <textarea
                className="form-control"
                rows={3}
                value={cancelReason}
                onChange={(e) => {
                  setCancelReason(e.target.value);
                  if (cancelModalError) {
                    setCancelModalError(null);
                  }
                }}
                placeholder={
                  cancelModalAction === 'void'
                    ? 'Please provide a reason for voiding...'
                    : 'Please provide a reason for cancellation...'
                }
                disabled={bulkProcessing}
              />
            </div>
            {cancelModalError && (
              <div className="alert alert-danger" style={{ marginTop: '12px', marginBottom: 0 }}>
                {cancelModalError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                  setCancelModalError(null);
                  setCancelModalInvoiceIds([]);
                  setCancelModalTargetLabel('');
                }}
                disabled={bulkProcessing}
              >
                Close
              </button>
              <button 
                className={cancelModalAction === 'void' ? 'btn btn-dark' : 'btn btn-danger'}
                onClick={submitCancelOrVoid}
                disabled={!cancelReason.trim() || bulkProcessing}
              >
                {bulkProcessing
                  ? (cancelModalAction === 'void' ? 'Voiding...' : 'Cancelling...')
                  : (cancelModalAction === 'void' ? 'Confirm Void' : 'Confirm Cancellation')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {confirmActionState && (
        <Modal
          title={confirmActionState.title}
          onClose={() => {
            if (!confirmActionLoading) {
              setConfirmActionState(null);
            }
          }}
        >
          <div>
            <p>{confirmActionState.message}</p>
            {(confirmActionState.type === 'bulk-send-to-accountant' || confirmActionState.type === 'bulk-send-to-accountant-individual') && (
              <>
                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label>Accountant Email Address *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={confirmActionState.accountantEmail || ''}
                    onChange={(e) =>
                      setConfirmActionState((prev) =>
                        prev ? { ...prev, accountantEmail: e.target.value } : prev
                      )
                    }
                    placeholder="accountant@company.com"
                    disabled={confirmActionLoading}
                  />
                  {confirmActionState.accountantEmail &&
                    !isValidEmail(confirmActionState.accountantEmail) && (
                      <small style={{ color: '#dc3545' }}>
                        Please enter a valid email address.
                      </small>
                    )}
                </div>
                <div className="form-group" style={{ marginTop: '10px' }}>
                  <label>Currencies *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '6px' }}>
                    {accountantCurrencyOptions.map((currency) => (
                      <label key={`accountant-${currency}`} style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={(confirmActionState.accountantCurrencies || []).includes(currency)}
                          onChange={(e) =>
                            setConfirmActionState((prev) => {
                              if (!prev) return prev;
                              const currentCurrencies = prev.accountantCurrencies || [];
                              return {
                                ...prev,
                                accountantCurrencies: e.target.checked
                                  ? [...currentCurrencies, currency]
                                  : currentCurrencies.filter((value) => value !== currency),
                              };
                            })
                          }
                          disabled={confirmActionLoading}
                        />{' '}
                        {currency}
                      </label>
                    ))}
                  </div>
                  {(confirmActionState.accountantCurrencies || []).length === 0 && (
                    <small style={{ color: '#dc3545' }}>Select at least one currency.</small>
                  )}
                </div>
              </>
            )}
            {(confirmActionState.type === 'bulk-upload-to-skalar' || confirmActionState.type === 'bulk-upload-to-skalar-individual') && (
              <>
                <div className="form-group" style={{ marginTop: '10px' }}>
                  <label>Currencies *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '6px' }}>
                    {accountantCurrencyOptions.map((currency) => (
                      <label key={`skalar-${currency}`} style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={(confirmActionState.accountantCurrencies || []).includes(currency)}
                          onChange={(e) =>
                            setConfirmActionState((prev) => {
                              if (!prev) return prev;
                              const currentCurrencies = prev.accountantCurrencies || [];
                              return {
                                ...prev,
                                accountantCurrencies: e.target.checked
                                  ? [...currentCurrencies, currency]
                                  : currentCurrencies.filter((value) => value !== currency),
                              };
                            })
                          }
                          disabled={confirmActionLoading}
                        />{' '}
                        {currency}
                      </label>
                    ))}
                  </div>
                  {(confirmActionState.accountantCurrencies || []).length === 0 && (
                    <small style={{ color: '#dc3545' }}>Select at least one currency.</small>
                  )}
                </div>
                <div className="alert alert-info" style={{ marginTop: '12px', marginBottom: 0 }}>
                  <strong>Note:</strong> This will upload invoices directly to Skalar via API instead of sending via email.
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmActionState(null)}
                disabled={confirmActionLoading}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmAction}
                disabled={
                  confirmActionLoading ||
                  ((confirmActionState.type === 'bulk-send-to-accountant' || confirmActionState.type === 'bulk-send-to-accountant-individual') &&
                    (
                      !isValidEmail(confirmActionState.accountantEmail || '') ||
                      (confirmActionState.accountantCurrencies || []).length === 0
                    ))
                }
              >
                {confirmActionLoading ? 'Processing...' : confirmActionState.confirmLabel}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {markPaidState && (
        <Modal
          title="Mark Invoice as Paid"
          onClose={() => {
            if (!markPaidLoading) {
              setMarkPaidState(null);
            }
          }}
        >
          <div>
            <p style={{ marginBottom: '12px' }}>
              Mark invoice <strong>{markPaidState.invoiceLabel}</strong> as paid.
            </p>
            <div className="form-group">
              <label>Payment Method</label>
              <input
                className="form-control"
                value={markPaidMethod}
                onChange={(e) => setMarkPaidMethod(e.target.value)}
                placeholder="bank_transfer"
                disabled={markPaidLoading}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setMarkPaidState(null)}
                disabled={markPaidLoading}
              >
                Close
              </button>
              <button
                className="btn btn-success"
                onClick={submitMarkAsPaid}
                disabled={markPaidLoading}
              >
                {markPaidLoading ? 'Updating...' : 'Confirm Paid'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Invoices;
